export default function CVEditPrivacy() {
  return (
    <main className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-[#A8720E]">
            Privacy Policy - CVEdit
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Effective date: <time dateTime="2026-04-23">April 23, 2026</time>
          </p>
          <p className="mt-1 text-xs text-[#8A93A2]">
            Published by <span className="font-semibold">Cosmovex</span>
          </p>
        </header>

        <div className="space-y-8 leading-relaxed text-[#3D4B59]">
          <p>
            <strong>Cosmovex</strong> built <strong>CVEdit</strong> as an
            ad-supported C programming editor and code runner. This Privacy
            Policy explains what information CVEdit may process, how that
            information is used, and the choices you have when using the app.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Information We Process
          </h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              <strong>C source files:</strong> code you write, open, or import
              is compiled and run locally on your device using the built-in C
              compiler. Your code is never uploaded to our servers.
            </li>
            <li>
              <strong>Program input and output:</strong> standard input you
              enter to feed into your programs, and standard output, error
              output, or compiler diagnostics produced by your programs, are
              displayed locally and are never transmitted to our servers.
            </li>
            <li>
              <strong>Practice progress:</strong> which practice problems you
              attempted, solved, or marked as favourite is stored locally on
              your device.
            </li>
            <li>
              <strong>App preferences:</strong> theme choice, editor settings,
              language choice, and Pro status are stored locally on your
              device.
            </li>
            <li>
              <strong>Usage and ad data:</strong> if ads are enabled, AdMob
              or related Google Mobile Ads services may collect device and
              advertising data as described in Google&apos;s own privacy
              documentation.
            </li>
            <li>
              <strong>Analytics and diagnostics:</strong> Firebase Analytics
              and Firebase Crashlytics may collect app stability data, usage
              patterns, and crash reports to improve reliability.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            How We Use Information
          </h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              To let you write, compile, and run C code directly on your
              device using the built-in compiler — no internet connection is
              required.
            </li>
            <li>
              To display program output, compiler errors, and plain-English
              explanations inline.
            </li>
            <li>
              To load the bundled practice problems and record your progress.
            </li>
            <li>
              To remember your editor settings, theme, and language choice.
            </li>
            <li>
              To improve app performance, reliability, and product quality.
            </li>
            <li>
              To display ads in supported versions of the app, where
              applicable.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">Local Storage</h2>
          <p>
            CVEdit is designed to keep your data on your device. C source
            files, saved snippets, practice progress, and preferences are
            stored locally and are not uploaded to any server. Data remains on
            your device until you remove it, clear the app&apos;s storage, or
            uninstall the app.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Offline Compilation and Execution
          </h2>
          <p>
            The built-in C compiler runs entirely on your device. When you
            tap Run, your source code is compiled in-process and the resulting
            program is executed inside the app&apos;s own sandbox. No network
            connection is used for compilation or execution, and no code is
            sent off your device.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Advertising</h2>
          <p>
            CVEdit may use <strong>Google AdMob</strong> to show banner,
            interstitial, and app-open ad formats. AdMob may collect and
            process identifiers, device information, approximate location, and
            ad interaction data to deliver and measure ads. You can remove ads
            with an optional one-time in-app purchase.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            In-App Purchases
          </h2>
          <p>
            CVEdit offers an optional one-time in-app purchase to remove
            advertisements. Purchases are processed through Google Play and
            are subject to Google&apos;s terms and privacy policies. There are
            no recurring charges.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Third-Party Services
          </h2>
          <p>CVEdit may rely on third-party services such as:</p>
          <ul className="list-disc space-y-2 pl-6">
            <li>Google Play Services</li>
            <li>Google AdMob</li>
            <li>Firebase Analytics</li>
            <li>Firebase Crashlytics</li>
            <li>Firebase Remote Config</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">Data Sharing</h2>
          <p>
            We do not sell your source code, practice progress, or personal
            data. Data may be shared only with service providers required to
            operate app features, such as advertising, analytics, app
            distribution, or diagnostics infrastructure.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Data Retention</h2>
          <p>
            Local app data remains on your device until you remove it, clear
            the app&apos;s storage, or uninstall the app. Data collected by
            third-party services such as AdMob and Firebase is retained
            according to those providers&apos; own policies.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Children&apos;s Privacy
          </h2>
          <p>
            CVEdit is not targeted at children under 13, and we do not
            knowingly collect personal information from children through the
            app.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Security</h2>
          <p>
            We take reasonable steps to protect data processed by the app,
            but no method of storage or transmission is completely secure.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Your Choices</h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              You can delete individual source files and saved snippets from
              inside the app.
            </li>
            <li>
              You can clear your practice progress from Settings.
            </li>
            <li>
              You can uninstall the app to remove all locally stored data
              from your device.
            </li>
            <li>You can purchase ad-free to disable all advertising.</li>
            <li>
              You can manage ad personalization and advertising controls
              through your device and Google settings.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Changes to This Policy
          </h2>
          <p>
            We may update this Privacy Policy from time to time. When we do,
            we will post the updated version on this page and revise the
            effective date.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Contact Us</h2>
          <p>
            For privacy-related questions about CVEdit, contact{" "}
            <a
              href="mailto:trionapps@gmail.com"
              className="text-[#0F6BBF] underline"
            >
              trionapps@gmail.com
            </a>
            .
          </p>
        </div>

        <footer className="mt-12 border-t border-[#E1DCCE] pt-4 text-sm text-[#8A93A2]">
          &copy; 2026 Cosmovex - All rights reserved.
        </footer>
      </section>
    </main>
  );
}
